<template>
  <section
    class="block-container def-block-container"
    :style="containerStyle"
    :data-style-variant="variant"
    :aria-labelledby="hasTitle ? titleId : null"
    :aria-label="!hasTitle ? computedAriaLabel : null"
  >
    <header class="block-container-header" :class="headerVariantClass" v-if="hasTitle">
      <component :is="headingTag" :id="titleId" class="block-container-header-title">{{ title }}</component>
    </header>
    <div class="block-container-content">
      <div class="top">
        <slot name="top"></slot>
      </div>

      <slot>
        <dl class="block-container-content-dl" v-if="rows && rows.length">
          <div class="block-container-content-dl-row" v-for="(row, idx) in rows" :key="row.key || idx">
            <dt class="block-container-content-dl-dt">{{ row.label }}:</dt>
            <dd class="block-container-content-dl-dd">{{ row.value }}</dd>
          </div>
        </dl>
      </slot>

      <div class="paragraph">
        <slot name="paragraph"></slot>
      </div>

      <div class="actions">
        <slot name="actions">
        </slot>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'BlockContainer',
  props: {
    title: {
      type: String,
      default: ''
    },
    sectionId: {
      type: String,
      default: ''
    },
    headingLevel: {
      type: [Number, String],
      default: 2
    },
    ariaLabel: {
      type: String,
      default: ''
    },
    flex: {
      type: String,
      default: null,
    },
    maxWidth: {
      type: [String, Number],
      default: null,
    },
    label:{
      type: String,
    },
    /** @type {"wide"|"narrow"|"full"|undefined} */
    variant: {
      type: String,
      default: null,
      validator: v => [null, 'wide', 'narrow', 'full'].includes(v)
    },
    /** @type {"main"|"secondary"|undefined} */
    headerVariant: {
      type: String,
      default: 'main',
      validator: v => ['main', 'secondary', null].includes(v)
    },
    /**
     * Optional convenience prop: if provided and no default slot content is passed,
     * it will render a <dl> with these label/value pairs.
     * Shape: [{ key?: string, label: string, value: string }]
     */
    rows: { type: Array, default: null },
  },
  computed: {
    normalizedHeadingLevel() {
      const level = Number(this.headingLevel);
      if (!Number.isInteger(level)) return 2;
      return Math.max(1, Math.min(level, 6));
    },
    headingTag() {
      return `h${this.normalizedHeadingLevel}`;
    },
    baseId() {
      return this.sectionId || `block-container-${this._uid}`;
    },
    titleId() {
      return `${this.baseId}-title`;
    },
    hasTitle() {
      return Boolean(this.title);
    },
    computedAriaLabel() {
      return this.ariaLabel || this.label || null;
    },
    containerStyle() {
      const style = {};
      if (this.flex) style.flex = this.flex;
      if (this.maxWidth) style.maxWidth = typeof this.maxWidth === 'number' ? `${this.maxWidth}px` : this.maxWidth;
      return style;
    },
    headerVariantClass() {
      return this.headerVariant === 'secondary' ? 'is-secondary' : null;
    }
  }
};
</script>

<style>
.block-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  margin: 0;
  flex: 1 1 400px;
  max-width: 100%;

  &[data-style-variant=wide] {
    flex: 2 1 640px;
    max-width: 800px;
  }
  &[data-style-variant=narrow]{
    flex: 1 1 280px;
    max-width: 360px;
  }
  &[data-style-variant=full] {
    flex: 1 1 100%;
    max-width: 100%;
  }
}

.block-container-header {
  position: relative;
}

.block-container-header-title {
  margin: 0;
}

.block-container-content {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
}

.block-container-content .actions {
  display: flex;
  flex-direction: column;
}
.block-container-content .actions > button:first-child {
  margin-top: 15px;
}
.block-container-content .actions > * + * {
  margin-top: .5rem;
}

.block-container-content .paragraph {
  display: flex;
  flex-direction: column;
}
.block-container-content .top {
  display: flex;
  flex-direction: column;
}
.block-container-content .top:empty {
  display: none;
}
.block-container-content .top > * + * {
  margin-top: .5rem;
}
.block-container-content .paragraph > *:first-child {
  margin-top: 15px;
}
.block-container-content .paragraph > * + * {
  margin-top: .5rem;
}

.block-container-content p {
  display:flex;
  flex-wrap:wrap;
  margin:0;
}
.block-container-content p > span:first-child {
  font-weight:bold;
}
.block-container-content p > span:last-child {margin-left:auto; text-align:right;}

.block-container-content-dl {
  margin: 0;
  row-gap: 6px;
}
.block-container-content-dl-row {
  display: grid;
  grid-template-columns: 110px 1fr;
  grid-column-gap: 10px;
}

.block-container-content-dl-dt {
  margin: 0;
  font-weight: 700;
}

.block-container-content-dl-dd {
  margin: 0;
}
</style>
